import * as React from "react"
import type { useCheckoutState } from "./useCheckoutState"

type Inputs = Pick<ReturnType<typeof useCheckoutState>,
    "journeyStep" | "stripeReady" | "differentBilling" | "billingAddress" | "billingMountRef" | "billingElementRef" | "checkoutRef" | "setBillingAddress" | "setBillingComplete">

export function useBillingAddress(inputs: Inputs) {
    const { journeyStep, stripeReady, differentBilling, billingAddress, billingMountRef, billingElementRef, checkoutRef, setBillingAddress, setBillingComplete } = inputs
    // Keep entered values when toggling the checkbox, without remounting the
    // Element (and losing focus) on each address change.
    const savedAddress = React.useRef(billingAddress)
    savedAddress.current = billingAddress
    React.useEffect(() => {
        setBillingComplete(false)
        if (journeyStep !== "payment" || !stripeReady || !differentBilling || !checkoutRef.current || !billingMountRef.current) return
        const saved = savedAddress.current
        const billing = checkoutRef.current.createBillingAddressElement({
            // Change events use null for empty fields; contacts only accept strings.
            ...(saved ? { contacts: [{
                name: saved.name || "",
                address: Object.fromEntries(Object.entries(saved.address).filter(([, value]) => typeof value === "string")),
            }] } : {}),
        })
        billingElementRef.current = billing
        billing.on("change", (event) => {
            if (billingElementRef.current !== billing) return
            setBillingComplete(event.complete === true)
            if (event.value) setBillingAddress(event.value)
        })
        billing.on("loaderror", () => {
            if (billingElementRef.current === billing) setBillingComplete(false)
        })
        billing.mount(billingMountRef.current)
        return () => {
            billing.unmount()
            billing.destroy?.()
            if (billingElementRef.current === billing) billingElementRef.current = null
            setBillingComplete(false)
        }
    }, [journeyStep, stripeReady, differentBilling])
}
